import React from 'react';
import SecondaryButton from '@/components/common/buttons/SecondaryButton';
import { Box, Clipboard, HStack, Text } from '@chakra-ui/react';

interface CopyableFieldProps {
	value: string;
	/**
	 * Render the value on a single line with an ellipsis instead of wrapping. Use for
	 * long values that aren't meant to be read in full (e.g. invite links), so the pill
	 * stays one row high.
	 */
	truncate?: boolean;
}

/**
 * A read-only pill that displays a copyable value (email, link) in mono type with
 * a Copy button. Shared so copy affordances stay visually consistent across settings.
 */
const CopyableField: React.FC<CopyableFieldProps> = ({ value, truncate = false }) => (
	<Clipboard.Root value={value}>
		<Box
			bg="blackAlpha.50"
			borderRadius={{ base: '3xl', md: 'full' }}
			pl={{ base: 3, md: 4 }}
			pr={2}
			py={2}
			w="full"
		>
			<HStack justify="space-between" gap={3}>
				<Text
					flex="1"
					minW={0}
					fontFamily="mono"
					fontSize={{ base: '14px', md: '16px' }}
					lineHeight="1.4"
					truncate={truncate}
					wordBreak={truncate ? undefined : 'break-all'}
					userSelect={truncate ? 'all' : undefined}
					title={truncate ? value : undefined}
				>
					{value}
				</Text>

				<Clipboard.Trigger asChild>
					<SecondaryButton
						bg="surface.default"
						color="brand.fg"
						border="1px solid"
						borderColor="border.default"
						borderRadius="full"
						textStyle="label"
						h={11}
						px={4}
						py={3}
						_hover={{ bg: 'blackAlpha.50' }}
						flexShrink={0}
					>
						Copy
						<Clipboard.Indicator />
					</SecondaryButton>
				</Clipboard.Trigger>
			</HStack>
		</Box>
	</Clipboard.Root>
);

export default CopyableField;
